"use client"

import { useSession } from "next-auth/react"
import { usePathname, useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { RAAUTH_ONLY } from "@/lib/auth-stack"
import { SSO_SKIP_PROBE_COOKIE, SSO_SYNC_COOKIE, ssoSkipProbeClearCookie, ssoSyncedSetCookie } from "@/lib/auth-cookies"
import { isSsoSilentPath, setSsoProbePhase, SSO_SILENT_PATH } from "./sso-probe-store"

/**
 * Silent SSO bootstrap (RAAUTH_ONLY only).
 *
 * RA can't read raauth's httpOnly session cookie, so the only way to know a user
 * is already logged in at raauth (e.g. from a sister site) is to ASK raauth. This
 * runs a background OIDC `prompt=none` request inside a hidden iframe:
 *   - raauth session exists → it returns a code → NextAuth's callback sets RA's session
 *     cookie → we re-read the session in place (no visible navigation).
 *   - no raauth session → raauth answers `login_required` (no UI) → the iframe lands on
 *     the error page, SsoSilentReporter tells us, and the user stays logged out.
 *
 * The probe is NOT conditional on any prior RA session. It can't be: the whole point is to
 * detect a session this browser only ever established somewhere else, so there is nothing
 * local to predict it from. It must fire on ordinary page loads — a user who deletes their
 * RA cookie, or arrives from a sister site, has to be signed back in without ever visiting
 * /unified/auth.
 *
 * A LOGGED-IN page load runs the same round trip for the opposite reason — a keep-alive.
 * RA's own cookie is re-issued on every session read and its raauth tokens are renewed
 * server-to-server, so an active user stays logged in here forever without the browser ever
 * reaching raauth again. raauth's SSO session, meanwhile, only rolls forward when the browser
 * DOES reach it (oidc-provider re-saves it and re-stamps the cookie in lib/shared/session.js,
 * and the authorization endpoint is one of only three provider routes that run that
 * middleware at all). Left alone the two clocks drift apart until the shared session dies
 * under a still-live RA session — at which point the user is logged into RA and logged out of
 * every sister site, which is exactly the bug QA reported. The keep-alive is throttled by
 * SSO_SYNC_COOKIE; the navbar never waits on it.
 *
 * ONE probe per page load, and no state that outlives the document. Earlier versions kept
 * cookie-based cooldowns (a 10s burst throttle, plus a browser-session "already asked"
 * marker) and re-armed them on tab focus/visibility. Every staleness bug in this flow came
 * from that machinery: the browser-session marker suppressed the probe for the rest of the
 * session, so logging out here, signing in on a sister site, and coming back in the same tab
 * never adopted the session — the focus re-arm can't help, because a same-tab navigation
 * builds a fresh document and no tab ever regains focus.
 *
 * Accepted trade-off: a user who signs in on a sister site while an RA tab sits open is not
 * detected until they reload or navigate. In exchange there is nothing to keep in sync, and
 * every page load asks raauth exactly once and gets a current answer.
 *
 * Never runs on auth pages (the login page owns the interactive flow) or inside an
 * iframe (would recurse). Everything is one registrable domain, so the iframe →
 * raauth request is same-site and carries the cookie even under third-party-cookie
 * blocking.
 */
// Backstop only. The normal answer arrives promptly via postMessage; this covers raauth
// being slow/unreachable, so the navbar can't hang on a skeleton forever.
const GIVE_UP_MS = 8000

// This flow has no server logs of its own and fails invisibly (a torn-down iframe looks
// identical to "no raauth session"), so trace the decision in non-production builds.
const trace = (reason: string, extra?: Record<string, unknown>) => {
   if (process.env.NODE_ENV !== "production") console.debug(`[silent-sso] ${reason}`, extra ?? "")
}

/** Present AND non-empty — a cleared cookie is still served as `name=` until it expires. */
function hasCookie(name: string): boolean {
   return document.cookie.split("; ").some((c) => {
      const eq = c.indexOf("=")
      return eq > 0 && c.slice(0, eq) === name && c.slice(eq + 1) !== ""
   })
}

/**
 * The document's single probe, spent. Module-scoped rather than a ref so that a React
 * strict-mode double-invoke — or any remount of this subtree — can't put up a second iframe.
 * It dies with the page load, which is the whole point: no cookie, no cross-tab channel, and
 * nothing that can be stale on the next navigation.
 */
let probeSpent = false

/**
 * What this document's one probe is FOR. Both kinds are the same `prompt=none` round trip;
 * they differ in what the answer means and what it may disturb.
 *
 *   adopt     — we are logged out. raauth may hold a session established on a sister site, and
 *               a hit signs the user in here. The navbar waits on the answer.
 *   keepalive — we are logged in. The answer is not interesting; the ROUND TRIP is, because
 *               reaching /oidc/auth is what re-saves raauth's SSO session and re-stamps its
 *               cookie. Runs in the background and the navbar never waits on it.
 *   restore   — a keepalive came back "no raauth session", i.e. this RA session has outlived
 *               (or never had) the SSO session behind it. Same round trip WITHOUT prompt=none
 *               so raauth mints an interaction that /unified/auth can complete from RA's own
 *               session. Also background.
 *
 * Module-scoped for the same reason as `probeSpent`: it belongs to the document, not to any
 * mount of this component.
 */
let probeKind: "adopt" | "keepalive" | "restore" = "adopt"

/** At most one restore attempt per document, whatever it concludes. */
let restoreSpent = false

export function SilentSsoCheck() {
   const { status, update } = useSession()
   const router = useRouter()
   const pathname = usePathname()
   const [show, setShow] = useState(false)
   // The worker route, as state: the restore leg re-points the same iframe at ?restore=1.
   const [iframeSrc, setIframeSrc] = useState(SSO_SILENT_PATH)
   // `update` gets a new identity on every session/loading change and `router` on every
   // navigation. Held in refs so the message listener below can stay mounted for the whole
   // probe: if it re-attached on every render it would also restart the give-up timer and
   // open windows where an arriving postMessage has no listener.
   const updateRef = useRef(update)
   updateRef.current = update
   const routerRef = useRef(router)
   routerRef.current = router

   /**
    * Stop the navbar waiting, WITHOUT claiming we know the answer. Used for every reason we
    * didn't probe that might not hold on the next page load — above all "we're currently
    * authenticated", which must not suppress a probe after the session cookie goes away.
    */
   const release = useCallback((reason: string) => {
      trace(`no probe: ${reason}`)
      setSsoProbePhase("settled")
      setShow(false)
   }, [])

   /**
    * The probe is over. Unless we were explicitly told there is no raauth session, re-read
    * the session. Nothing is recorded anywhere — `probeSpent` already stops this document
    * asking twice, and the next page load is entitled to a fresh answer.
    *
    * `reread` is deliberately true on TIMEOUT as well as success: NextAuth's callback sets
    * the cookie server-side, so the cookie can exist even when we never hear back (a dropped
    * postMessage, a slow callback — 4s is normal here — or a client-side navigation that
    * replaced the iframe mid-flight). Re-reading is one request and turns every one of those
    * into a successful sign-in instead of a silent failure.
    */
   const finishProbe = useCallback(async (reason: string, reread: boolean) => {
      trace(`probe finished: ${reason}`, { kind: probeKind, reread })
      setSsoProbePhase("settled")
      setShow(false)

      // Any probe that ran at all reached /oidc/auth, and reaching /oidc/auth is what rolls
      // raauth's SSO session forward. Record it whatever the answer was — including "no raauth
      // session", where retrying on every page load would be a request per page for nothing.
      document.cookie = ssoSyncedSetCookie(window.location.host)

      if (probeKind === "adopt") {
         if (!reread) return
         const session = await updateRef.current()
         trace(session ? "session adopted" : "no session after re-read")
         // useSession() only updates client state; server components (and anything derived
         // from auth() on the server) still hold the logged-out render until the route is
         // refetched.
         if (session) routerRef.current.refresh()
         return
      }

      // keepalive / restore. The user was already authenticated either way, so there is
      // nothing to adopt and nothing that renders differently.
      if (reread) {
         // The round trip re-issued RA's cookie server-side, so resync the client's copy.
         // Deliberately no router.refresh(): refetching the route on a background probe is
         // pure cost on whatever page the user happened to land on when the window opened.
         await updateRef.current()
         return
      }

      // We are authenticated here and raauth is not: sister sites will bounce this user to a
      // login screen while RA still renders them signed in — the exact split QA reported.
      if (probeKind === "keepalive" && !restoreSpent) {
         // RA can prove who this is (a live raauth grant), and /unified/auth will hand that
         // proof to a fresh interaction. Run the same round trip without prompt=none so
         // raauth actually mints one. See app/api/auth/sso-restore/route.ts.
         trace("ORPHANED: no raauth SSO session — attempting silent restore")
         restoreSpent = true
         probeKind = "restore"
         setIframeSrc(`${SSO_SILENT_PATH}?restore=1`)
         setShow(true)
         return
      }

      trace("ORPHANED: no raauth SSO session, and restore did not take")
   }, [])

   const maybeCheck = useCallback(() => {
      // Every early return must either release the navbar or leave the phase already set by
      // whoever got here first — otherwise it waits on an answer that is never coming and sits
      // on a skeleton. The two exceptions are commented at their return.
      if (!RAAUTH_ONLY) return release("raauth stack disabled")
      if (typeof window === "undefined") return
      if (window.self !== window.top) return release("inside an iframe")
      // Auth pages drive their own (interactive) OIDC redirect — don't race them.
      if (pathname?.startsWith("/unified") || pathname?.startsWith("/v2/unified") || isSsoSilentPath(pathname)) {
         return release("on an auth page")
      }
      if (status === "loading") return // not a decision yet — wait for the session to resolve

      // Authenticated: there is nothing to adopt, but raauth's SSO session may be quietly
      // ageing out underneath this one. RA's cookie is re-issued on every session read and its
      // raauth tokens are renewed server-to-server, so RA can stay logged in indefinitely
      // without the browser ever reaching raauth again — while the shared SSO session, which
      // only rolls when the browser DOES reach it, runs down on a fixed clock. Roll it here,
      // at most once per sync window. See SSO_SYNC_COOKIE.
      if (status === "authenticated") {
         // Someone already spent the document's probe — an adopt probe that succeeded, most
         // likely, which rolled the session on its way through. Must not release: that probe
         // owns the phase and may still be in flight.
         if (probeSpent) return
         if (hasCookie(SSO_SYNC_COOKIE)) return release("raauth session rolled recently")
         trace("rolling raauth's SSO session (prompt=none keep-alive)")
         probeSpent = true
         probeKind = "keepalive"
         // Settled, not "probing": the navbar already has a session to render and must never
         // wait on a background keep-alive.
         setSsoProbePhase("settled")
         setShow(true)
         return
      }

      if (status !== "unauthenticated") return release("session status unavailable")
      // The document's one probe is already running or done, and whoever spent it set the
      // phase ("probing", or "settled" if it has finished). Must NOT release: a strict-mode
      // double-invoke lands here immediately after the first pass raised the iframe, and
      // settling now would tear down a probe that is still in flight.
      if (probeSpent) return
      // One-shot marker from the logout redirect: we know there is no raauth session left, so
      // don't ask. Consume it here — reading it is what limits it to this single page load —
      // and spend the document's probe, so an explicit logout stays a logout for this page.
      if (hasCookie(SSO_SKIP_PROBE_COOKIE)) {
         document.cookie = ssoSkipProbeClearCookie(window.location.host)
         probeSpent = true
         return release("just logged out")
      }

      trace("probing raauth (prompt=none)")
      probeSpent = true
      setSsoProbePhase("probing")
      setShow(true)
   }, [status, pathname, release])

   // Once per page load, re-running only until the session status resolves into a decision
   // (and on client-side navigation off an auth page, where we deliberately don't probe).
   // `probeSpent` keeps that to a single iframe. No focus/visibility listeners: re-checking a
   // live document is what created every staleness bug this flow used to have.
   useEffect(() => {
      maybeCheck()
   }, [maybeCheck])

   useEffect(() => {
      if (!show) return
      const onMessage = (e: MessageEvent) => {
         if (e.origin !== window.location.origin) return
         if (e.data?.type !== "ra-sso-done") return
         // ok === false is an explicit "raauth has no session" from SsoSilentReporter, so
         // there is genuinely nothing to re-read. Anything else: re-read.
         if (e.data.ok === false) void finishProbe("no raauth session", false)
         else void finishProbe("adopted a raauth session", true)
      }
      window.addEventListener("message", onMessage)
      const timer = setTimeout(() => void finishProbe("timed out", true), GIVE_UP_MS)
      return () => {
         window.removeEventListener("message", onMessage)
         clearTimeout(timer)
      }
      // `iframeSrc` is a dependency so the restore leg gets its OWN give-up timer. Without it
      // the keep-alive's timer would keep running and could cut the restore short.
   }, [show, iframeSrc, finishProbe])

   if (!show) return null
   return (
      <iframe
         // Keyed on the src so re-pointing it at the restore leg remounts the frame rather
         // than relying on a same-element src swap.
         key={iframeSrc}
         src={iframeSrc}
         title="sso"
         aria-hidden
         tabIndex={-1}
         style={{ position: "absolute", width: 0, height: 0, border: 0, visibility: "hidden" }}
      />
   )
}
